import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CaseStatus } from '../../../../enums/case-status.enum';
import { ConsultationHistoryModel } from '../../../core/models/consultation/consultation-history.model';
import { ChatService } from '../../../core/services/chat/chat.service';
import { ChatStatus } from '../../../../enums/chat-status.enum';

export enum TimeLineColors {
  active = 'var(--bs-primary)',
  closed = 'var(--bs-gray-300)',
  closedText = 'var(--bs-gray-400)',
  hasSubCases = 'var(--bs-dark)',
}

@Component({
  selector: 'app-case-timeline',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './case-timeline.component.html',
  styleUrl: './case-timeline.component.scss',
  providers: [DatePipe],
})
export class CaseTimelineComponent implements OnInit, AfterViewInit {
  ChatStatus = ChatStatus;
  @Input() consultationId: string = '';
  @Input() consultationTimeLines: ConsultationHistoryModel[] = [];

  @Input() selectedTimeline: ConsultationHistoryModel;
  @Output() selectedTimelineChange = new EventEmitter<any>();

  constructor(
    private chatService: ChatService,
    private translate: TranslateService
  ) {
    // const userLang = this.languageService.getLanguage() || 'en';
    // this.translate.use(userLang);
    const userLang = localStorage.getItem('selectedLanguage') || 'en';
    this.translate.use(userLang);
    // this.createPatientImageConnection();
  }

  caseStatus = CaseStatus;
  timeLineDates: string[] = [];

  timelineWidth: string; // '20%'

  ngOnInit(): void {
    // this.getCases();
    // this.manipulateTimelineForSameReportedDate();
    this.timeLineDates = this.setTimelineDates();
    this.timelineWidth = this.getTimelineItemWidth();
    this.unreadChatCount();
  }

  ngAfterViewInit(): void {
    this.listenDomForSelectedCase();
    this.setSelectedClasses();
  }

  /**
   * If reportDate and createdDate is same day manipulate reportDate +1
   */
  manipulateTimelineForSameReportedDate() {
    this.consultationTimeLines.map((x) => {
      if (
        !!x.reportDate &&
        this.compareDates(x.createdDate, x.reportDate) === 'same'
      ) {
        const newDate = new Date(x.reportDate);
        newDate.setDate(newDate.getDate() + 1);
        x.reportDate = newDate.toISOString();
      }
      return x;
    });
  }

  setTimelineDates() {
    const allCases = this.consultationTimeLines.flatMap(this.getRecursiveCases);

    const dates = allCases.flatMap((caseItem: ConsultationHistoryModel) => {
      const createdDate =
        caseItem.createdDate && new Date(caseItem.createdDate);
      const reportedDate =
        caseItem.reportDate &&
        caseItem.isCaseClosed &&
        new Date(caseItem.reportDate);

      const formatDate = (date: Date) => {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      };

      const createdDateResult = createdDate && formatDate(createdDate);
      const reportedDateResult = reportedDate && formatDate(reportedDate);
      const result = [];
      createdDateResult && result.push(createdDateResult);
      reportedDateResult && result.push(reportedDateResult);
      return result;
    });

    return [...new Set(dates)].sort((a: string, b: string) => {
      const [dayA, monthA, yearA] = a.split('-').map(Number);
      const [dayB, monthB, yearB] = b.split('-').map(Number);

      const dateA = new Date(yearA, monthA - 1, dayA).getTime();
      const dateB = new Date(yearB, monthB - 1, dayB).getTime();

      return dateA - dateB;
    });
  }

  onSelectTimeline(caseItem: ConsultationHistoryModel, event?: any) {
    event?.preventDefault();
    event?.stopPropagation();
    if (
      this.selectedTimeline.patientRecordCaseId === caseItem.patientRecordCaseId
    ) {
      return;
    }
    this.selectedTimeline = caseItem;
    this.selectedTimelineChange.emit(this.selectedTimeline);
  }

  hasTimelineBall(
    caseItem: ConsultationHistoryModel,
    timelineDate: string
  ): ConsultationHistoryModel | null {
    // if(timelineDate === this.timeLineDates[0]) return caseItem;
    // if (!caseItem.cases?.length ) return null;
    const subCases: ConsultationHistoryModel[] =
      this.getRecursiveCases(caseItem);
    return (
      subCases.find((x) => {
        const createdDate = x.createdDate && this.manipulateDate(x.createdDate);
        if (
          createdDate &&
          this.compareDates(createdDate, timelineDate) === 'same'
        ) {
          return true;
        }
        return false;
      }) || null
    );
  }

  hasTimelineBar(caseItem: ConsultationHistoryModel, timelineDate: string) {
    // if(caseItem.cases?.length && caseItem.reportDate) return false
    const createdDate =
      caseItem.createdDate && this.manipulateDate(caseItem.createdDate);
    if (!createdDate) return false;
    const createdDateResult = this.compareDates(createdDate, timelineDate); // same

    const flatCases = this.flatCases(caseItem?.cases);
    const lastFlatCase = flatCases[flatCases.length - 1];
    const comparedFlatCase = this.compareDates(lastFlatCase, timelineDate);

    if (
      caseItem.cases?.length &&
      comparedFlatCase === 'case-bigger' &&
      createdDateResult !== 'case-bigger'
    )
      return true;

    const reportDate =
      caseItem.reportDate && this.manipulateDate(caseItem.reportDate);
    // if (createdDateResult !== 'case-bigger' && !reportDate) return true;
    if (!reportDate) return false;
    const reportDateResult = this.compareDates(reportDate, timelineDate); //case-bigger
    if (
      createdDateResult !== 'case-bigger' &&
      reportDateResult === 'case-bigger'
    )
      return true;
    return false;
  }

  flatCases(cases: any[]): string[] {
    return [
      ...new Set(cases.map((caseItem: any) => caseItem.createdDate)),
    ].sort((a: string, b: string) => {
      const [dayA, monthA, yearA] = a.split('-').map(Number);
      const [dayB, monthB, yearB] = b.split('-').map(Number);

      const dateA = new Date(yearA, monthA - 1, dayA).getTime();
      const dateB = new Date(yearB, monthB - 1, dayB).getTime();

      return dateA - dateB;
    });
  }

  manipulateDate(dateString: string | Date) {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  compareDates(
    caseDate: string,
    timelineDate: string
  ): 'same' | 'case-bigger' | 'case-younger' {
    const d1 = this.parseDate(caseDate);
    const d2 = this.parseDate(timelineDate);

    const caseDateStartOfDay = new Date(
      d1.getFullYear(),
      d1.getMonth(),
      d1.getDate()
    );
    const timelineDateStartOfDay = new Date(
      d2.getFullYear(),
      d2.getMonth(),
      d2.getDate()
    );
    if (caseDateStartOfDay.getTime() === timelineDateStartOfDay.getTime()) {
      return 'same';
    } else if (caseDateStartOfDay > timelineDateStartOfDay) {
      return 'case-bigger';
    } else {
      return 'case-younger';
    }
  }

  parseDate(dateString: string) {
    if (dateString?.includes('T')) {
      const [year, month, day] = dateString
        .split('T')[0]
        .split('-')
        .map(Number);
      return new Date(year, month - 1, day); // Gün-ay-yıl formatı
    }
    if (dateString?.includes('-')) {
      const [day, month, year] = dateString.split('-').map(Number);
      return new Date(year, month - 1, day); // Gün-ay-yıl formatı
    }
    return new Date(dateString);
  }

  getRecursiveCases(caseItem: ConsultationHistoryModel) {
    const result = [];
    function recurse(item) {
      if (item.cases) {
        item.cases.forEach((subItem) => recurse(subItem));
      }
      result.push(item);
    }

    recurse(caseItem);
    return result;
  }

  getTimelineItemWidth() {
    const count = this.timeLineDates.length || 1;
    return `${100 / count}%`;
  }

  getTimelineBarBGColor(caseItem: ConsultationHistoryModel) {
    let result: TimeLineColors;
    if (caseItem.cases?.length) result = TimeLineColors.active;
    // else if (!caseItem.reportDate) result = TimeLineColors.active;
    // else result = TimeLineColors.closed;
    return result;
  }

  getTimelineCircleBGColor(caseItem: ConsultationHistoryModel, item) {
    let result: TimeLineColors;
    if (caseItem.cases?.length) {
      const hasCaseClosed = caseItem.cases.some((item) => item.isCaseClosed);
      if (hasCaseClosed) {
        return TimeLineColors.closed;
      } else {
        return TimeLineColors.active;
      }
    } else if (!caseItem.isCaseClosed) result = TimeLineColors.active;
    else result = TimeLineColors.closed;
    return result;
  }

  getTimelineTextColor(caseItem: ConsultationHistoryModel) {
    let result: TimeLineColors;
    if (caseItem.cases?.length) result = TimeLineColors.active;
    else if (!caseItem.isCaseClosed) result = TimeLineColors.active;
    else result = TimeLineColors.closedText;
    return result;
  }

  getTimelineBarClass(caseItem: ConsultationHistoryModel, index: number) {
    if (index === this.timeLineDates?.length) return '';
    const reportDate =
      caseItem.reportDate && this.manipulateDate(caseItem.reportDate);
    if (
      reportDate &&
      this.compareDates(reportDate, this.timeLineDates[index + 1]) === 'same'
    ) {
      return 'end';
    }
    return '';
  }

  listenDomForSelectedCase() {
    const container = document.querySelector('.timeline-area');
    if (!container) {
      return;
    }
    const observer = new MutationObserver(() => {
      this.setSelectedClasses(container);
    });

    observer.observe(container, {
      attributes: true,
      subtree: true,
      attributeFilter: ['class'],
    });
  }

  setSelectedClasses(container?: Element) {
    if (!container) container = document.querySelector('.timeline-area');
    const selectedItem = container.querySelector('.case-item.selected');
    if (!selectedItem) {
      return;
    }
    // .timeline-space öğelerini al
    const timelineSpaces = selectedItem.querySelectorAll('.timeline-space');
    if (!timelineSpaces) {
      return;
    }
    // Son .timeline-space öğesini seç
    const lastTimelineSpace = Array.from(timelineSpaces)
      .reverse()
      .find((space) => space.querySelector('.timeline-bar'));
    if (
      !!lastTimelineSpace &&
      !lastTimelineSpace.classList.contains('selected-border-end')
    ) {
      // Son elemana selected-border-end ekle
      lastTimelineSpace.classList.add('selected-border-end');

      // son timelime-space öğesinden önceki tüm öğelere selected-border ekle
      let isLastTimelineSpaceReached = false;

      timelineSpaces.forEach((space) => {
        if (space === lastTimelineSpace) {
          isLastTimelineSpaceReached = true;
        }
        if (!isLastTimelineSpaceReached) {
          space.classList.add('selected-border');
        }
      });
    }
  }

  async unreadChatCount() {
    const caseIds = this.consultationTimeLines.map(
      (caseItem) => caseItem.patientRecordCaseId
    );

    const form: any = {
      patientRecordCaseIds: caseIds,
    };

    this.chatService.getUnreadChatCount(form).subscribe({
      next: async (res) => {
        this.consultationTimeLines = this.consultationTimeLines.map(
          (caseItem) => {
            const matchedResult = res.find(
              (resultItem: any) =>
                resultItem.patientRecordCaseId === caseItem.patientRecordCaseId
            );
            return {
              ...caseItem,
              unreadCount: matchedResult ? matchedResult.unreadCount : 0,
            };
          }
        );
      },
      error: () => {},
    });
  }
}
